import { GoogleGenAI, Modality } from "@google/genai";

// Initialize AI lazily
let aiClient: GoogleGenAI | null = null;

async function getAI() {
    if (aiClient) return aiClient;
    
    try {
        // Try multiple ways to get the key in browser/server environments
        let key = null;
        
        // 1. Check process.env (Node/Some bundlers)
        if (typeof process !== 'undefined' && process.env) {
            key = process.env.GEMINI_API_KEY || process.env.API_KEY;
            
            // Search for custom names if standard ones not found
            if (!key) {
                const envKeys = Object.keys(process.env);
                const customKey = envKeys.find(k => k.includes('GEMINI') || k.includes('API_KEY'));
                if (customKey) key = process.env[customKey];
            }
        }
        
        // 2. Check import.meta.env (Vite)
        if (!key && typeof import.meta !== 'undefined' && (import.meta as any).env) {
            const env = (import.meta as any).env;
            key = env.VITE_GEMINI_API_KEY || env.VITE_API_KEY;
        }

        // 3. Robust cleaning if found
        if (key) {
            const potentialKeys = [
                String(key || ""),
                String(import.meta.env.VITE_GEMINI_API_KEY || ""),
                String((window as any)._env_?.VITE_GEMINI_API_KEY || ""),
                String((window as any).process?.env?.VITE_GEMINI_API_KEY || ""),
                String((window as any).process?.env?.GEMINI_API_KEY || "")
            ];

            let cleanKey = "";
            // Priority 1: Find a valid looking key (starts with AIza)
            for (const k of potentialKeys) {
                const val = k.trim();
                if (val && val.startsWith('AIza') && val.length > 30) {
                    cleanKey = val;
                    break;
                }
            }

            // Priority 2: Try cleaning if not found (remove quotes, prefixes etc)
            if (!cleanKey) {
                for (const k of potentialKeys) {
                    let val = k.trim().replace(/^["']|["']$/g, '').trim();
                    if (val.includes(':') || val.includes('=')) {
                        const lastPart = val.split(/[:=]/).pop()?.trim();
                        if (lastPart?.startsWith('AIza')) val = lastPart;
                    }
                    if (val && val.startsWith('AIza') && val.length > 30) {
                        cleanKey = val;
                        break;
                    }
                }
            }

            if (cleanKey && cleanKey.startsWith('AIza')) {
                console.log(`[CLIENT AUTH] AI Initialized successfully`);
                aiClient = new GoogleGenAI({ apiKey: cleanKey });
                return aiClient;
            } else {
                console.warn("[CLIENT AUTH] API Key needs to start with AIza to be valid.");
            }
        }
    } catch (e) {
        console.warn("AI Client Init failed:", e);
    }
    
    return null;
}

export async function generateSpeech(text: string, voiceName: string, tone: string = 'standard', manualSeed?: number): Promise<string> {
    try {
        const MAX_CHUNK_SIZE = 850; // Updated to 850 to prevent quality degradation
        
        // Helper to split text by sentences/paragraphs while respecting max size
        const splitText = (input: string): string[] => {
            const paragraphs = input.split(/\n+/);
            const chunks: string[] = [];
            let currentChunk = "";

            for (const p of paragraphs) {
                if ((currentChunk + p).length < MAX_CHUNK_SIZE) {
                    currentChunk += (currentChunk ? "\n" : "") + p;
                } else {
                    if (currentChunk) chunks.push(currentChunk);
                    
                    // If a single paragraph is too long, split by sentences
                    if (p.length >= MAX_CHUNK_SIZE) {
                        const sentences = p.split(/(?<=[.!?])\s+/);
                        for (const s of sentences) {
                            if ((currentChunk + s).length < MAX_CHUNK_SIZE) {
                                currentChunk = (currentChunk ? " " : "") + s;
                            } else {
                                if (currentChunk) chunks.push(currentChunk);
                                currentChunk = s;
                                // Handle extreme case: sentence longer than chunk limit
                                while (currentChunk.length >= MAX_CHUNK_SIZE) {
                                    chunks.push(currentChunk.substring(0, MAX_CHUNK_SIZE));
                                    currentChunk = currentChunk.substring(MAX_CHUNK_SIZE);
                                }
                            }
                        }
                    } else {
                        currentChunk = p;
                    }
                }
            }
            if (currentChunk) chunks.push(currentChunk);
            return chunks;
        };

        const textChunks = splitText(text);
        
        // Ensure voice consistency by using the same seed for all chunks
        // If manualSeed is provided, use it. Otherwise, generate a random one once.
        const effectiveSeed = manualSeed !== undefined ? manualSeed : Math.floor(Math.random() * 2147483647);

        if (textChunks.length <= 1) {
            return await generateSpeechSingle(text, voiceName, tone, effectiveSeed, false);
        }

        console.log(`Processing long text in ${textChunks.length} chunks with seed ${effectiveSeed}...`);
        
        // Generate audio for each chunk
        const base64Chunks = [];
        for (let i = 0; i < textChunks.length; i++) {
            const chunk = textChunks[i];
            const isContinuation = i > 0;
            const audioData = await generateSpeechSingle(chunk, voiceName, tone, effectiveSeed, isContinuation);
            base64Chunks.push(audioData);
        }

        // Return the concatenated audio
        return base64Chunks.join(''); 
        
    } catch (error) {
        console.error("Error in grouped speech generation:", error);
        throw error;
    }
}

async function generateSpeechSingle(text: string, voiceName: string, tone: string = 'standard', manualSeed?: number, isContinuation: boolean = false): Promise<string> {
    const ai = await getAI();
    
    // Attempt client-side generation first
    if (ai) {
        try {
            console.log("Attempting client-side speech generation...");
            let actualVoiceName = voiceName;
            let voiceInstruction = "";
            
            if (voiceName === 'Child') {
                actualVoiceName = 'Zephyr'; 
                voiceInstruction = "ACTING INSTRUCTION: You are a real young child, about 6 years old. Your voice is naturally high-pitched, light, and soft. Do not use an adult's vocal weight or sophisticated resonance. Speak with innocent curiosity, simple phrasing, and the natural enthusiasm of a child playing. Avoid sounding like an adult mimicking a child; use your highest and lightest vocal registers naturally.";
            } else if (voiceName === 'Fenrir') {
                voiceInstruction = "ACTING INSTRUCTION: You are a world-class international news broadcaster (like BBC Swahili). Speak with absolute authority, mature presence, and clear articulation. Your voice must sound masculine, commanding, and professional. Use a dry, studio-focused delivery, minimizing any resonance or echo (punguza mwangi) for a grounded and direct sound.";
            } else if (voiceName === 'Puck') {
                voiceInstruction = "ACTING INSTRUCTION: You have a naturally deep and resonant male voice. Speak effortlessly with your natural bass register. Do not force the depth or sound like you are acting; simply let the natural, low-frequency weight of your voice carry the words with a relaxed and professional delivery.";
            }

            let toneInstruction = "Speak in a natural, standard, and clear narrative tone.";
            switch(tone) {
                case 'cheerful': toneInstruction = "Speak in a consistently cheerful tone."; break;
                case 'formal': toneInstruction = "Speak in a professional, formal tone."; break;
                case 'calm': toneInstruction = "Speak in a very calm, soft tone."; break;
                case 'excited': toneInstruction = "Speak with great excitement."; break;
            }
            
            if (isContinuation) {
                toneInstruction += " IMPORTANT NOTE: This text is a smooth continuation of a longer recording that you are actively narrating. DO NOT start with a fresh, reset, or altered pitch. Seamlessly maintain the exact same energy, pitch level, and flow from the previous sentence.";
            }

            const prompt = `Role: Professional Swahili Narrator. ${voiceInstruction} Tone Instruction: ${toneInstruction} Text: ${text}`;
            
            const response = await ai.models.generateContent({
                model: "gemini-3.1-flash-tts-preview",
                contents: [{ parts: [{ text: prompt }] }],
                config: {
                    responseModalities: [Modality.AUDIO],
                    speechConfig: {
                        voiceConfig: {
                            prebuiltVoiceConfig: { voiceName: actualVoiceName as any },
                        },
                    },
                },
            });
            
            const base64Audio = response.candidates?.[0]?.content?.parts?.find(p => p.inlineData)?.inlineData?.data;
            if (base64Audio) return base64Audio;
        } catch (error) {
            console.error("Client-side Speech Gen Error, falling back to proxy:", error);
        }
    }

    // Fallback to Server-side Proxy
    console.log("Calling backend speech generation proxy...");
    try {
        const response = await fetch('/api/generate-speech', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text, voiceName, tone, manualSeed, isContinuation })
        });
        
        if (!response.ok) {
            const responseText = await response.text();
            let errorMessage = `Server error (${response.status})`;
            try {
                const errData = JSON.parse(responseText);
                errorMessage = errData.error || errorMessage;
            } catch (e) {
                // If it's HTML, we don't want to show the whole page, just the status
                console.error("Non-JSON error response:", responseText.substring(0, 200));
            }
            throw new Error(errorMessage);
        }
        
        const responseText = await response.text();
        try {
            const data = JSON.parse(responseText);
            return data.audio;
        } catch (e) {
            console.error("Failed to parse JSON response:", responseText.substring(0, 200));
            throw new Error("Server returned invalid data format.");
        }
    } catch (error: any) {
        console.error("Speech Proxy Error:", error);
        
        // Pass through the specific error message from the backend if it exists
        const finalMessage = error.message || "Imeshindwa kutengeneza sauti. Tafadhali jaribu tena.";
            
        throw new Error(finalMessage);
    }
}

export async function transcribeAudio(base64Audio: string, mimeType: string): Promise<string> {
    const ai = await getAI();
    
    if (ai) {
        try {
            const response = await ai.models.generateContent({
                model: "gemini-3-flash-preview",
                contents: [{
                    parts: [
                        { inlineData: { mimeType, data: base64Audio } },
                        { text: "Transcribe the audio text verbatim." }
                    ]
                }]
            });
            return response.text || "No transcription.";
        } catch (error) {
            console.error("Client-side Transcription Error, falling back:", error);
        }
    }

    // Proxy Fallback
    try {
        const response = await fetch('/api/transcribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ audioData: base64Audio, mimeType })
        });
        if (!response.ok) throw new Error("Proxy transcription failed");
        const data = await response.json();
        return data.text;
    } catch (error: any) {
        console.error("Transcription Error:", error);
        throw new Error("Imeshindikana kusoma sauti kwasasa.");
    }
}

export async function translateText(text: string, targetLanguage: 'Swahili' | 'English'): Promise<string> {
    const ai = await getAI();
    
    if (ai) {
        try {
            const response = await ai.models.generateContent({
                model: "gemini-3-flash-preview",
                contents: [{ parts: [{ text: `Translate the following text to ${targetLanguage}. Return ONLY the translated Swahili or English text: ${text}` }] }]
            });
            return response.text || "Translation failed.";
        } catch (error) {
            console.error("Client-side Translation Error, falling back:", error);
        }
    }

    // Proxy Fallback
    try {
        const response = await fetch('/api/translate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text, targetLanguage })
        });
        if (!response.ok) throw new Error("Proxy translation failed");
        const data = await response.json();
        return data.translatedText;
    } catch (error: any) {
        console.error("Translation Error:", error);
        throw new Error("Tafsiri imefeli kwasasa.");
    }
}

export async function getTextCompletion(text: string): Promise<string> {
    const ai = await getAI();
    
    if (ai) {
        try {
            const response = await ai.models.generateContent({
                model: "gemini-3-flash-preview",
                contents: [{ parts: [{ text: `Role: AI Writing Assistant for Swahili Text to Speech studio. 
                Context: User is writing Swahili text for audio generation. 
                Instruction: Provide a natural continuation (predictive text) of the following sentence. 
                Rules:
                1. Return ONLY the predicted following words (max 10 words).
                2. Do not repeat the input. 
                3. Keep it professional and natural. 
                Input: "${text}"` }] }]
            });
            return response.text || "";
        } catch (error) {
            console.error("Text Completion Error:", error);
            return "";
        }
    }
    return "";
}
